/**
 * Pure geometry seam for the scroll affordance (edge fades over the browser's own scrollbar).
 *
 * `scrollGeometry -> affordanceState`, per the Spec, plus all of the rectangle arithmetic the
 * affordance needs. Takes no DOM: every input is a plain number, or a plain rectangle of numbers,
 * already available from a scroll container (`scrollTop`/`scrollLeft`, `clientHeight`/`clientWidth`,
 * `scrollHeight`/`scrollWidth`, `clientTop`/`clientLeft`, `getBoundingClientRect()`).
 *
 * Rectangles live here rather than beside the axes so there is one home for them: `Bounds`,
 * `fadeBoxOf`, `intersectionOf` and `isEmpty` are one body of arithmetic and split badly.
 *
 * Both axes are computed independently by the same function; the caller decides which axis (or
 * both) it paints. See `scroll-affordance.ts` for the discovery + paint half.
 */

/** The plain numbers already available from a scroll container, on one axis. */
export type ScrollGeometry = {
   /** `scrollTop` on the vertical axis, `scrollLeft` on the horizontal one. */
   scrollOffset: number;
   /** `clientHeight` / `clientWidth` — how much of the content is visible. */
   visibleLength: number;
   /** `scrollHeight` / `scrollWidth` — the full content length. */
   totalLength: number;
};

/** What to draw for one axis. */
export type AxisAffordance = {
   /** top (left) */
   fadeAtStart: boolean;
   /** bottom (right) */
   fadeAtEnd: boolean;
};

/** Both axes, decided independently. */
export type ScrollAffordanceGeometry = {
   vertical: ScrollGeometry;
   horizontal: ScrollGeometry;
};

export type ScrollAffordanceState = {
   vertical: AxisAffordance;
   horizontal: AxisAffordance;
};

// -- Rectangles ----------------------------------------------------------------------------------

/**
 * A rectangle in viewport coordinates. A `DOMRect` satisfies this, so any rect read from the DOM can
 * be handed straight to the functions below.
 */
export type Bounds = {
   left: number;
   top: number;
   right: number;
   bottom: number;
};

export function intersectionOf(first: Bounds, second: Bounds): Bounds {
   return {
      left: Math.max(first.left, second.left),
      top: Math.max(first.top, second.top),
      right: Math.min(first.right, second.right),
      bottom: Math.min(first.bottom, second.bottom),
   };
}

export function isEmpty(bounds: Bounds): boolean {
   return bounds.right - bounds.left <= 0 || bounds.bottom - bounds.top <= 0;
}

/**
 * The measurements that locate one panel's **client box** — its padding box — inside the border box
 * the paint half already reads.
 *
 * The client box is the box the fades are drawn in, because it is the only box that excludes both
 * the panel's borders and whatever gutter the platform's own scrollbar takes. Measuring the gutter as
 * "border box minus client box" instead would count the two borders as scrollbar: on a bordered panel
 * that pulls the fade in short of the scrollbar at one edge and pushes it out over the border at the
 * other.
 */
export type PanelBox = {
   /** The panel's border box, in viewport coordinates (`getBoundingClientRect()`). */
   borderBox: Bounds;
   /**
    * `clientLeft`: the distance from the border box's left edge to the client box's — the left border
    * width, plus the scrollbar itself where the platform puts it on the left (`direction: rtl`).
    */
   clientOffsetLeft: number;
   /** `clientTop`: the same distance on the top edge — the top border width. */
   clientOffsetTop: number;
   /** `clientWidth`: borders and any vertical scrollbar already excluded. */
   clientWidth: number;
   /** `clientHeight`: borders and any horizontal scrollbar already excluded. */
   clientHeight: number;
};

/**
 * The box the fades are drawn in: the panel's client box, placed in viewport coordinates. Pure —
 * takes measurements, not an element, so it stays testable without a layout engine.
 *
 * Where the platform draws a classic scrollbar this stops the fades exactly at the scrollbar's edge
 * and off the panel's borders. Where it draws an overlay scrollbar, which takes no width at all, the
 * client box is the border box inset by the borders alone and the fade runs the full width of the
 * content: there is no scrollbar there to protect.
 */
export function fadeBoxOf(panel: PanelBox): Bounds {
   const left = panel.borderBox.left + panel.clientOffsetLeft;
   const top = panel.borderBox.top + panel.clientOffsetTop;

   return {
      left,
      top,
      right: left + panel.clientWidth,
      bottom: top + panel.clientHeight,
   };
}

// -- The fade-edge decision ----------------------------------------------------------------------

/**
 * A scroll offset within this many px of an end counts as that end, and content within this many px
 * of its container's length counts as fitting. Layouts hand back fractional
 * `scrollHeight`/`clientHeight` values, so a panel that is "really" fully scrolled can still read a
 * hair short of the maximum offset — without this, the fade at that edge would never turn off.
 *
 * Exported because discovery applies the same rule when it decides whether a candidate scrolls at
 * all: an element this function would give no fade to must not become a scroller in the first place.
 */
export const EDGE_EPSILON = 1;

/**
 * Decides one axis. Pure; no DOM. Deliberately not exported: `scrollAffordanceState` below is the
 * whole seam, and it is what the paint half calls, so there is exactly one way in for both
 * production and the tests.
 */
function axisAffordance(geometry: ScrollGeometry): AxisAffordance {
   const { scrollOffset, visibleLength, totalLength } = geometry;
   const scrollable = totalLength - visibleLength;

   // Content fits: nothing to show on this axis.
   if (scrollable <= EDGE_EPSILON) {
      return { fadeAtStart: false, fadeAtEnd: false };
   }

   return {
      fadeAtStart: scrollOffset > EDGE_EPSILON,
      fadeAtEnd: scrollOffset < scrollable - EDGE_EPSILON,
   };
}

/** Decides both axes. The whole seam the Spec names. */
export function scrollAffordanceState(geometry: ScrollAffordanceGeometry): ScrollAffordanceState {
   return {
      vertical: axisAffordance(geometry.vertical),
      horizontal: axisAffordance(geometry.horizontal),
   };
}
